import React, { useEffect, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { platformSettingsService } from '../../../services/central-api/platform-settings';
import { useAuth } from '../../../context/Auth';
import { View } from 'react-native';
import { translate } from '../../../services/translate';
import CustomButton from '../../../components/CustomButton';
import Toast from 'react-native-toast-message';
import MyAppText from '../../../components/MyAppText';
import { MyCheckbox } from '../../../components/MyCheckbox';
import { TextInputMask } from 'react-native-masked-text';
import { ScrollView } from 'react-native-web-hover';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

export default function AccessCredentials() {
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);

    const [passwordRotationDays, setPasswordRotationDays] = useState(0);
    const [inactivityDaysToBlockLogin, setInactivityDaysToBlockLogin] = useState(0);
    const [requireTwoFactorAuthentication, setRequireTwoFactorAuthentication] = useState(true);

    const isFocused = useIsFocused();
    const { data, refetch } = useQuery({
        queryKey: ['platformSettingsAccessCredentials'],
        queryFn: () => platformSettingsService.getAccessCredentials(),
        enabled: !!authenticatedUser && isFocused,
    });

    useEffect(() => {
        if (!data) {
            return;
        }
        setPasswordRotationDays(data.passwordRotationDays);
        setInactivityDaysToBlockLogin(data.inactivityDaysToBlockLogin);
        setRequireTwoFactorAuthentication(data.requireTwoFactorAuthentication);
    }, [data]);

    async function saveAccessCredentials() {
        try {
            await platformSettingsService.updateAccessCredentials({
                passwordRotationDays,
                inactivityDaysToBlockLogin,
                requireTwoFactorAuthentication,
            });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return <>
        <ScrollView>
            <View style={{ flex: 1, gap: 20 }}>
                <View style={{ flex: 1, gap: 10 }}>
                    <MyAppText style={styles.fieldLabel}>
                        {translate('passwordRotationDays')}
                    </MyAppText>
                    <View style={[{ flexDirection: 'row' }]}>
                        <TextInputMask
                            value={String(passwordRotationDays)}
                            includeRawValueInChangeText={true}
                            onChangeText={(maskedText, rawText) => {
                                setPasswordRotationDays(Number(rawText));
                            }}
                            type={'only-numbers'}
                            style={[styles.inputAttached, { maxWidth: 70, textAlign: 'center' }]}
                        />
                        <MyAppText style={styles.inputAttach}>{translate('days')}</MyAppText>
                    </View>
                    <MyAppText style={styles.fieldExplanation}>
                        {translate('passwordRotationDaysExplanation')}
                    </MyAppText>
                </View>
                <View style={{ flex: 1, gap: 10 }}>
                    <MyAppText style={styles.fieldLabel}>
                        {translate('MaxInactivityPeriod')}
                    </MyAppText>
                    <View style={[{ flexDirection: 'row' }]}>
                        <TextInputMask
                            value={String(inactivityDaysToBlockLogin)}
                            includeRawValueInChangeText={true}
                            onChangeText={(maskedText, rawText) => {
                                setInactivityDaysToBlockLogin(Number(rawText));
                            }}
                            type={'only-numbers'}
                            style={[styles.inputAttached, { maxWidth: 70, textAlign: 'center' }]}
                        />
                        <MyAppText style={styles.inputAttach}>{translate('days')}</MyAppText>
                    </View>
                    <MyAppText style={styles.fieldExplanation}>
                        {translate('maxInactivityPeriodExplanation')}
                    </MyAppText>
                </View>
                <View style={{ gap: 4 }}>
                    <MyCheckbox
                        label={translate('requireTwoFactorAuthentication')}
                        checked={requireTwoFactorAuthentication}
                        setChecked={setRequireTwoFactorAuthentication}
                    />
                    <MyAppText style={styles.fieldExplanation}>
                        {translate('requireTwoFactorAuthenticationExplanation')}
                    </MyAppText>
                </View>
            </View>
        </ScrollView>
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
            <CustomButton
                onPress={saveAccessCredentials}
                text={translate('save')}
            />
        </View>
    </>;
}

const styleSheet = createStyleSheet((theme) => ({
    fieldLabel: {
        color: theme.colors.labelColor,
        fontSize: 14,
        fontWeight: 'bold',
    },
    fieldExplanation: {
        color: theme.colors.card.detailText,
        fontSize: 14,
    },
    inputAttached: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.container.background,
        maxHeight: 40,
        padding: 10,
        color: theme.colors.textColor,
        borderWidth: 1,
        borderColor: theme.colors.container.border,
        borderTopStartRadius: 4,
        borderBottomStartRadius: 4,
    },
    inputAttach: {
        color: theme.colors.textColor,
        fontSize: 13,
        backgroundColor: theme.colors.container.border,
        maxHeight: 40,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderRightWidth: 1,
        borderColor: theme.colors.container.border,
        borderTopEndRadius: 4,
        borderBottomEndRadius: 4,
        padding: 11,
    },
}));
